import { Card } from 'primereact/card';
import styles from '../AnalysisWizardSteps.module.css';
import { InputText } from 'primereact/inputtext';
import { Divider } from 'primereact/divider';
import { DocumentSelector } from '../../DocumentSelector/DocumentSelector';
import { Button } from 'primereact/button';
import { useState } from 'react';
import {
  AnalysisCreateRequest,
  useCheckAnalysisNameAvailability
} from '@/api/domains/analyses';
import { DocumentSummary } from '@/api/domains/documents';
import { Checkbox } from '@/components/ui';
import { ANALYSIS_ENABLED } from '@/config/features';

interface StepSelectExternalProps {
  formData: Omit<AnalysisCreateRequest, 'user_id'>;
  setFormData: (data: Omit<AnalysisCreateRequest, 'user_id'>) => void;
  documents: DocumentSummary[];
  handleNext: () => void;
  disableSimilaritySearch: boolean;
  setDisableSimilaritySearch: (value: boolean) => void;
}

const StepSelectExternal = ({
  formData,
  setFormData,
  documents = [],
  handleNext,
  disableSimilaritySearch,
  setDisableSimilaritySearch
}: StepSelectExternalProps) => {
  const [analysisNameAvailable, setAnalysisNameAvailable] =
    useState<Boolean | null>(null);

  const nameAvailableMutation = useCheckAnalysisNameAvailability();

  const canProceedStep1 =
    formData.document_1_id !== '' &&
    formData.analysis_name.trim() !== '' &&
    analysisNameAvailable === true;

  const checkAnalysisNameAvailability = async () => {
    if (!formData.analysis_name.trim()) return;

    try {
      const available = await nameAvailableMutation.mutateAsync(
        formData.analysis_name
      );
      setAnalysisNameAvailable(available);
    } catch (error) {
      console.error('Error checking name availability:', error);
      setAnalysisNameAvailable(false);
    }
  };

  const toggleDisableSimilarity = (checked: boolean) => {
    setDisableSimilaritySearch(checked);
  };

  return (
    <Card className={styles.stepCard}>
      <div className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>Select External Document</h2>
        <p className={styles.stepDescription}>
          Choose the external document (this will be compared against an
          internal document).
        </p>
      </div>

      <div className={styles.formFields}>
        <div className={styles.field}>
          <label htmlFor="analysisName" className={styles.label}>
            Analysis Name <span className={styles.required}>*</span>
          </label>
          <div className={styles.inputContainer}>
            <InputText
              id="analysisName"
              value={formData.analysis_name}
              onChange={(e) =>
                setFormData({
                  ...(formData as any),
                  analysis_name: e.target.value
                } as Omit<AnalysisCreateRequest, 'user_id'>)
              }
              onBlur={checkAnalysisNameAvailability}
              placeholder="e.g., MaRisk vs Internal Backup Policy"
              className={`${styles.input} ${
                analysisNameAvailable === true
                  ? styles.inputAvailable
                  : analysisNameAvailable === false
                    ? styles.inputUnavailable
                    : ''
              }`}
            />
            {analysisNameAvailable !== null && (
              <div
                className={`${styles.availabilityIndicator} ${
                  analysisNameAvailable ? styles.available : styles.unavailable
                }`}
              >
                <i
                  className={
                    analysisNameAvailable ? 'pi pi-check' : 'pi pi-times'
                  }
                />
              </div>
            )}
          </div>
          {analysisNameAvailable === false && (
            <small className={`${styles.fieldHint} ${styles.unavailableText}`}>
              Name is already taken
            </small>
          )}
          <small className={styles.fieldHint}>
            Enter a unique name for this analysis
          </small>
        </div>

        <Divider />

        <div className={styles.field}>
          <Checkbox
            inputId="disableSimilaritySearch"
            checked={disableSimilaritySearch}
            onChange={(checked) => toggleDisableSimilarity(checked)}
            disabled={!ANALYSIS_ENABLED}
            label="Disable similarity search"
            hint="Optional: when checked, the similarity search won't run in the next step."
          />
        </div>

        <Divider />

        <DocumentSelector
          documents={documents}
          selectedDocumentId={formData.document_1_id}
          onSelect={(sourceUrl) =>
            setFormData({
              ...(formData as any),
              analysis_name: formData.analysis_name,
              document_1_id: sourceUrl,
              document_2_id: ''
            } as Omit<AnalysisCreateRequest, 'user_id'>)
          }
          title="Available External Documents"
          description="Select the external document (this will be compared against an internal document)"
          emptyMessage="No documents available."
        />
      </div>

      <div className={styles.stepActions}>
        <div></div>
        <Button
          label="Next"
          icon="pi pi-arrow-right"
          iconPos="right"
          onClick={handleNext}
          disabled={!canProceedStep1}
          className={styles.primaryButton}
        />
      </div>
    </Card>
  );
};

export default StepSelectExternal;
